"use client";

import { CalendarDaysIcon, ChevronDownIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { FC } from "react";
import Link from "next/link";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import type { AutoPublishChange, AutoPublishState } from "../../hooks/useAutoPublish";
import type { SocialProviderAccount } from "../../hooks/useSocialProviderAccounts";
import AccountsSelectDropdown, { AccountsAvatarStack, formatCadenceRangeLabel } from "../AccountsSelectDropdown";
import Button, { BUTTON_STYLE } from "../Button";
import CheckboxInput from "../CheckboxInput";
import PropertyTile from "../PropertyTile";
import {
  DEFAULT_ACCOUNT_CADENCE,
  DEFAULT_PREFERRED_HOURS,
  getAccountCadence,
  getPreferredHoursForCadence,
} from "../publicationFlow/engine/types";
import SchedulePickerDropdown from "../SchedulePickerDropdown";
import Spinner from "../Spinner";
import { AUTO_POST } from "./autoPostName";
import AutoPostInfo, { AUTO_POST_INFO_TOOLTIP_CLASS } from "./AutoPostInfo";
import AutoPostWaitingStatus from "./AutoPostWaitingStatus";

import { AUTO_POST_STATUS, autoPublishSummary, getAutoPostQueueText, type AutoPostQueue } from "./autoPostStatus";

// The Auto publish popover's body, for the results header and the submit
// form: connect an account first, then the switch, what it does (off) or
// where the posts stand (on), and the accounts and hours chips.
// Presentational: useAutoPublishSwitch handles the switch.

// A failed profile lookup can produce a temporary placeholder. Only a
// confirmed token failure should ask the user to reconnect.
const getPickerAccounts = (accounts: SocialProviderAccount[], rawAccounts: SocialProviderAccount[]) =>
  rawAccounts.filter(account => account.provider !== "linkedin" &&
    (account.needsReconnect || accounts.some(target => target.id === account.id)));

/**
 * For the popovers holding the chips: the Starts calendar is portaled to the
 * body, so a click in it (a day, the next month) must not read as outside.
 */
export const keepOpenOnCalendarClick = (event: MouseEvent) => {
  if (event.composedPath().some((node) => node instanceof Element && node.matches(".react-datepicker-popper, .react-datepicker")))
    event.preventDefault();
};

export const AutoPublishChips: FC<{
  className?: string;
  state: AutoPublishState;
  accounts: SocialProviderAccount[];
  rawAccounts?: SocialProviderAccount[];
  disabled?: boolean;
  onReconnect?: (account: SocialProviderAccount) => void;
  onChange: (change: AutoPublishChange) => void;
  onConnect: () => void;
}> = ({ className, state, accounts, rawAccounts = accounts, disabled = false, onReconnect, onChange, onConnect }) => {
  const isMobile = useMediaQuery("(max-width: 767px)", { initializeWithValue: false });
  const selectedAccountIds = state.accountIds.filter((id) => accounts.some((account) => account.id === id));
  const pickerAccounts = getPickerAccounts(accounts, rawAccounts);
  const reconnectAccountIds = pickerAccounts.filter(account => account.needsReconnect).map(account => account.id);
  const reconnectCount = state.accountIds.filter(id => reconnectAccountIds.includes(id)).length;
  const cadences = new Map(Object.entries(state.cadences));
  // The union of the selected accounts' slots. Each account takes its own
  // cadence-sized prefix of this project's priority pool.
  const hours = getPreferredHoursForCadence(
    state.preferredHours ?? DEFAULT_PREFERRED_HOURS,
    Math.max(1, ...selectedAccountIds.map((id) => getAccountCadence(cadences, id).maxPostsPerDay)),
  );
  return (
    <div className={twMerge("flex flex-wrap items-center gap-1 -mx-2", isMobile && reconnectCount === 0 && "grid grid-cols-2 max-[359px]:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] w-full mx-0", className)}>
      {pickerAccounts.length === 0 ? (
        <Button type="button" variant="tertiary" size="m" disabled={disabled} onClick={onConnect}>
          <Button.Text>Connect account</Button.Text>
        </Button>
      ) : <AccountsSelectDropdown
        size="m"
        positions={["bottom", "top"]}
        align="start"
        accounts={pickerAccounts}
        selectedAccountIds={state.accountIds}
        reconnectAccountIds={reconnectAccountIds}
        onReconnectAccount={account => { if (!disabled) (onReconnect ? onReconnect(account) : onConnect()); }}
        // From the saved list, not the one shown: an account that needs
        // reconnecting stays in the project (and keeps its posts).
        onToggleAccount={(id) => { if (!disabled) onChange({
          accountIds: state.accountIds.includes(id)
            ? state.accountIds.filter((selected) => selected !== id)
            : [...state.accountIds, id],
        }); }}
        onManageAccounts={() => { if (!disabled) onConnect(); }}
        accountCadences={cadences}
        onChangeAccountCadence={(id, cadence) => { if (!disabled) onChange({ cadences: { [id]: cadence } }); }}
        showDistributionHint
        cadenceScopeHint="Cadence is shared across Auto post projects using this account."
        trigger={
          <Button
            type="button"
            variant="tertiary"
            size="m"
            align="start"
            disabled={disabled}
            className={twMerge("group min-w-0 max-w-60 shrink truncate", isMobile && "w-full max-w-none max-[359px]:pl-2")}
            data-tooltip-id="global"
            data-tooltip-content={reconnectCount > 0 ? "Reconnect your disconnected accounts to resume posting to them" : "Accounts and posting cadence"}
          >
            {reconnectCount > 0 ? <ExclamationTriangleIcon className="text-amber-600 dark:text-amber-400" /> : selectedAccountIds.length > 0 ? (
              <Button.Content>
                <AccountsAvatarStack accounts={accounts} selectedAccountIds={selectedAccountIds} triggerVariant="tertiary" maxAvatars={isMobile ? 1 : undefined} />
              </Button.Content>
            ) : null}
            <Button.Text>
              {reconnectCount > 0
                ? `Reconnect account${reconnectCount === 1 ? "" : "s"}`
                : selectedAccountIds.length === 0 ? "Select accounts"
                : formatCadenceRangeLabel(selectedAccountIds.map((id) => cadences.get(id) ?? DEFAULT_ACCOUNT_CADENCE))}
            </Button.Text>
            <ChevronDownIcon />
          </Button>
        }
      />}
      {/* Saved as the Schedule page saves it: the shown hours, then the rest of the pool.
          Hours and Starts affect this project; as soon as possible shows its first post's day. */}
      <SchedulePickerDropdown
        hours={hours}
        fullWidth={isMobile}
        maxSummaryTimes={isMobile ? 1 : undefined}
        onChangeHours={(next) => onChange({
          preferredHours: [...[...next].sort((a, b) => a - b), ...(state.preferredHours ?? DEFAULT_PREFERRED_HOURS).slice(hours.length)],
        })}
        startDate={state.startAt ?? undefined}
        effectiveStartDate={state.nextPostAt ?? undefined}
        onStartDateChange={(date) => onChange({ startAt: date ?? null })}
      />
    </div>
  );
};

/** The popover's body: the switch with what it does, or once on where the posts stand, then the chips.
 * The chips show off too, so the plan the switch would make is in view before the flip (the caller
 * passes the switch's `shownState`, with the accounts turning on would pick). Nothing else: two more
 * lines of explanation read as a terms sheet and bought no clarity in reading tests. */
export const AutoPublishPanel: FC<{
  state: AutoPublishState | null;
  accounts: SocialProviderAccount[];
  /** Retain disconnected destinations so the user knows what needs reconnecting. */
  rawAccounts?: SocialProviderAccount[];
  onReconnect?: (account: SocialProviderAccount) => void;
  status?: string | null;
  /** Confirmed project processing state; independent of optimistic switch edits. */
  waitingForShorts?: boolean;
  /** A forecast replaces the waiting label when the first posting time can be estimated. */
  firstPostEstimate?: string | null;
  /** Source failure is independent of saved settings and already-booked posts. */
  sourceFailure?: string | null;
  /** Unavailable sources show an inactive switch without overwriting saved intent. */
  sourceBlocked?: boolean;
  /** Confirmed queue used by the subtitle; never the optimistic settings. */
  queue?: AutoPostQueue;
  /** Existing projects can open their posts without changing Auto post settings. */
  calendarHref?: string;
  onShowInCalendar?: () => void;
  shortCount?: number;
  /** Switching on is waiting on the plan: a spinner in the switch's place. */
  checking?: boolean;
  /** Accessible spinner label only; loading never replaces the visible subtitle. */
  busyLabel?: string;
  disabled?: boolean;
  /** The pointer or focus reached the switch: ask the plan ahead. */
  onSwitchIntent?: () => void;
  onChange: (change: AutoPublishChange) => void;
  onConnect: () => void;
}> = ({ state, accounts, rawAccounts = accounts, status, waitingForShorts = false, firstPostEstimate, sourceFailure, sourceBlocked = false, queue, calendarHref, onShowInCalendar, shortCount, checking = false, busyLabel, disabled = false, onSwitchIntent, onChange, onConnect, onReconnect }) => {
  // Off with nowhere to post, connecting is the only step. On, the switch
  // stays even with no account left, so its booked posts can still be removed.
  const needsAccount = state !== null && !state.enabled && getPickerAccounts(accounts, rawAccounts).length === 0;
  const queueText = queue ? getAutoPostQueueText(queue) : null;
  const showWaiting = waitingForShorts && Boolean(state) && !sourceFailure && !sourceBlocked && !queueText;
  return (
  <>
    <PropertyTile
      title={AUTO_POST}
      info={<AutoPostInfo />}
      infoTooltipClassName={AUTO_POST_INFO_TOOLTIP_CLASS}
      titleOverflowVisible={Boolean(queueText && calendarHref)}
      subtitle={<>
        {sourceFailure ? <span className="block text-red-500">{sourceFailure}</span> : null}
        {queueText && calendarHref ? (
        <span className="flex flex-wrap items-center gap-x-0.5 gap-y-0.5">
          {queueText.nextPost ? <span className="whitespace-nowrap">{queueText.nextPost}</span> : null}
          <Button asChild variant="tertiary" size="xs"
            className="whitespace-nowrap font-normal text-inherit [&>div>span]:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <Link href={calendarHref} onClick={onShowInCalendar} aria-label={`Show ${queueText.countLabel} in calendar`}>
              <CalendarDaysIcon />
              <Button.Text>{queueText.compactCountLabel}</Button.Text>
            </Link>
          </Button>
        </span>
        ) : sourceFailure ? queueText?.summary : showWaiting ? (
          firstPostEstimate ? (
            <span>
              {firstPostEstimate}<span className="sr-only">. {AUTO_POST_STATUS.waitingForShorts}</span>
            </span>
          ) : <AutoPostWaitingStatus />
        ) : status ?? autoPublishSummary(shortCount)}
      </>}
      trailing={needsAccount ? undefined : !state || checking || busyLabel ? (
        <span className={`${BUTTON_STYLE.s.height} ${BUTTON_STYLE.s.width} flex items-center justify-center`} role="status" aria-label={busyLabel ?? (!state ? AUTO_POST_STATUS.loading : AUTO_POST_STATUS.checking)}>
          <Spinner className="size-4 text-slate-400" />
        </span>
      ) : (
        <span className="flex" title={sourceBlocked ? AUTO_POST_STATUS.sourceBlocked : undefined}
          onPointerEnter={sourceBlocked ? undefined : onSwitchIntent} onPointerDown={sourceBlocked ? undefined : onSwitchIntent}
          onFocus={sourceBlocked ? undefined : onSwitchIntent}>
          <CheckboxInput size="s" aria-label={sourceBlocked ? AUTO_POST_STATUS.sourceBlocked : AUTO_POST}
            disabled={disabled || sourceBlocked} checked={state.enabled && !sourceBlocked}
            onValueChange={(on) => { if (!sourceBlocked) onChange({ enabled: on }); }} />
        </span>
      )}
    />
    {state ? (
      // Match the bottom inset to the sides: 16px for the full-width CTA,
      // 8px for the chips, whose labels line up with the title.
      <div className={twMerge("flex flex-col gap-2 px-4 pt-1 pb-2", needsAccount && "pb-4")}>
        {needsAccount ? (
          <Button type="button" variant="primary" size="m" className="w-full"
            disabled={disabled || checking || Boolean(busyLabel)} onClick={onConnect}>
            <Button.Text>Connect account</Button.Text>
          </Button>
        ) : (
          <AutoPublishChips state={state} accounts={accounts} rawAccounts={rawAccounts}
            disabled={disabled || checking || Boolean(busyLabel)} onReconnect={onReconnect} onChange={onChange} onConnect={onConnect} />
        )}
      </div>
    ) : null}
  </>
);
};
